import { Input } from '@heidi/design-system/input';
import { TypographyP2 } from '@heidi/design-system/typography';
import { FileText } from 'lucide-react';
import { useId, useMemo, useState, type JSX } from 'react';
import { useIntl, type IntlShape } from 'react-intl';

import { CalendarApprovalCard } from './calendar-approval-card';
import { InlineApprovalActions } from './inline-approval-actions';
import { InlineApprovalCardShell } from './inline-approval-card-shell';
import { LabelledContextPanel } from './labelled-context-panel';
import { MailApprovalCard } from './mail-approval-card';
import {
  Field,
  useNangoApprovalSubmit,
  type NangoConnectorApprovalCardProps,
} from './nango-approval-submit';
import {
  buildFileModifiedInput,
  nangoConnectorApprovalKind,
  readFileApprovalFields,
  readWorkspaceWriteApprovalFields,
  type FileApprovalFields,
  type NangoWorkspaceWriteAction,
  type WorkspaceWriteApprovalFields,
} from './nango-connector-approval';

export { useNangoApprovalSubmit } from './nango-approval-submit';
export type {
  NangoConnectorApprovalCardProps,
  NangoConnectorApprovalResolve,
} from './nango-approval-submit';

export function NangoConnectorApprovalCard({
  approval,
  onResolve,
}: NangoConnectorApprovalCardProps): JSX.Element | null {
  const kind = nangoConnectorApprovalKind(approval.tool_name, approval.tool_input);
  if (kind === 'mail') {
    return <MailApprovalCard approval={approval} onResolve={onResolve} />;
  }
  if (kind === 'calendar') {
    return <CalendarApprovalCard approval={approval} onResolve={onResolve} />;
  }
  if (kind === 'file') {
    return <FileApprovalCard approval={approval} onResolve={onResolve} />;
  }
  if (kind === 'workspace') {
    return <WorkspaceWriteApprovalCard approval={approval} onResolve={onResolve} />;
  }
  return null;
}

function FileApprovalCard({ approval, onResolve }: NangoConnectorApprovalCardProps) {
  const intl = useIntl();
  const fieldId = useId();
  const { pendingVerdict, isBusy, submit } = useNangoApprovalSubmit(
    approval.approval_id,
    onResolve
  );
  const initial = useMemo(
    () => readFileApprovalFields(approval.tool_name, approval.tool_input),
    [approval.tool_input, approval.tool_name]
  );
  const [filename, setFilename] = useState(() => initial?.filename ?? '');

  if (!initial) return null;

  const currentFields = (): FileApprovalFields => ({
    filename,
    folderPath: initial.folderPath,
  });

  return (
    <InlineApprovalCardShell
      Icon={FileText}
      title={intl.formatMessage({
        defaultMessage: 'Save file',
        id: 'F2ujCMSH3z',
      })}
      subtitle={intl.formatMessage({
        defaultMessage: 'Review the file name before it is saved',
        id: 'c+b0oz8cQ7',
      })}
      urgent={!isBusy}
      data-testid={`nango-file-approval-${approval.approval_id}`}
    >
      <div className="flex flex-col gap-3">
        <Field
          id={`${fieldId}-filename`}
          label={intl.formatMessage({ defaultMessage: 'File name', id: 'ppAn7OQ0yB' })}
        >
          <Input
            id={`${fieldId}-filename`}
            value={filename}
            onChange={(event) => setFilename(event.target.value)}
            disabled={isBusy}
            data-testid="nango-file-approval-filename"
          />
        </Field>
        {initial.folderPath ? (
          <TypographyP2 className="text-text-secondary">
            {intl.formatMessage(
              {
                defaultMessage: 'Folder: {folder}',
                id: 'qGw+2biPT5',
              },
              { folder: initial.folderPath }
            )}
          </TypographyP2>
        ) : null}
        <InlineApprovalActions
          onApprove={() =>
            submit(
              'approve',
              buildFileModifiedInput(approval.tool_input, currentFields())
            )
          }
          onDeny={() => submit('deny')}
          pendingVerdict={pendingVerdict}
          disabled={isBusy}
          approveDisabled={filename.trim().length === 0}
        />
      </div>
    </InlineApprovalCardShell>
  );
}

function WorkspaceWriteApprovalCard({
  approval,
  onResolve,
}: NangoConnectorApprovalCardProps) {
  const intl = useIntl();
  const { pendingVerdict, isBusy, submit } = useNangoApprovalSubmit(
    approval.approval_id,
    onResolve
  );
  const fields = useMemo(
    () => readWorkspaceWriteApprovalFields(approval.tool_name, approval.tool_input),
    [approval.tool_input, approval.tool_name]
  );

  if (!fields) return null;

  return (
    <InlineApprovalCardShell
      Icon={FileText}
      title={workspaceWriteTitle(intl, fields)}
      subtitle={intl.formatMessage({
        defaultMessage: 'This writes to a connected app',
        id: 'KG36cvDpLp',
      })}
      urgent={!isBusy}
      data-testid={`nango-workspace-approval-${approval.approval_id}`}
    >
      <div className="flex flex-col gap-3">
        <LabelledContextPanel
          label={intl.formatMessage({
            defaultMessage: 'What this does',
            id: 'Mno4r2Qwe2',
          })}
        >
          <TypographyP2 className="text-sm text-text-secondary">
            {workspaceWriteSummary(intl, fields)}
          </TypographyP2>
        </LabelledContextPanel>
        {fields.destination ? (
          <LabelledContextPanel
            label={intl.formatMessage({
              defaultMessage: 'Destination',
              id: 'BzQPM+4Est',
            })}
          >
            <TypographyP2
              className="break-all text-sm text-text-secondary"
              data-testid="nango-workspace-approval-destination"
            >
              {fields.destination}
            </TypographyP2>
          </LabelledContextPanel>
        ) : null}
        {fields.preview ? (
          <LabelledContextPanel
            label={intl.formatMessage({
              defaultMessage: 'Preview',
              id: 'TJo5E6JuIN',
            })}
          >
            <TypographyP2
              className="whitespace-pre-wrap break-words text-sm text-text-secondary"
              data-testid="nango-workspace-approval-preview"
            >
              {fields.preview}
            </TypographyP2>
          </LabelledContextPanel>
        ) : null}
        <InlineApprovalActions
          onApprove={() => submit('approve')}
          onDeny={() => submit('deny')}
          pendingVerdict={pendingVerdict}
          disabled={isBusy}
        />
      </div>
    </InlineApprovalCardShell>
  );
}

const workspaceWriteTitle = (
  intl: IntlShape,
  fields: WorkspaceWriteApprovalFields
): string => {
  const action = fields.action;
  if (action === 'create') {
    return intl.formatMessage({
      defaultMessage: 'Create in connected app',
      id: '4DY8qH7mOW',
    });
  }
  if (action === 'delete') {
    return intl.formatMessage({
      defaultMessage: 'Delete in connected app',
      id: 'qhI2kll9ST',
    });
  }
  if (action === 'send') {
    return intl.formatMessage({
      defaultMessage: 'Send to connected app',
      id: 'AQ+TNsgp4c',
    });
  }
  return intl.formatMessage({
    defaultMessage: 'Edit in connected app',
    id: '889QBJ/YjG',
  });
};

const workspaceWriteSummary = (
  intl: IntlShape,
  fields: WorkspaceWriteApprovalFields
): string => {
  const actionLabel = workspaceActionLabel(intl, fields.action);
  const targetLabel = workspaceTargetLabel(intl, fields.target);
  return intl.formatMessage(
    {
      defaultMessage: '{action} a {target}.',
      id: 'pPDmY2WqRO',
    },
    { action: actionLabel, target: targetLabel }
  );
};

const workspaceActionLabel = (
  intl: IntlShape,
  action: NangoWorkspaceWriteAction
): string => {
  if (action === 'create') {
    return intl.formatMessage({ defaultMessage: 'Create', id: 'VzzYJkHnLP' });
  }
  if (action === 'delete') {
    return intl.formatMessage({ defaultMessage: 'Delete', id: 'K3r6DQW7h+' });
  }
  if (action === 'send') {
    return intl.formatMessage({ defaultMessage: 'Send', id: '9WRlF4R2gm' });
  }
  return intl.formatMessage({ defaultMessage: 'Edit', id: 'wEQDC6Wv3/' });
};

const workspaceTargetLabel = (
  intl: IntlShape,
  target: WorkspaceWriteApprovalFields['target']
): string => {
  if (target === 'spreadsheet') {
    return intl.formatMessage({ defaultMessage: 'spreadsheet', id: '9Ushpsi4Al' });
  }
  if (target === 'page') {
    return intl.formatMessage({ defaultMessage: 'page', id: 'ovCNnFs/tg' });
  }
  if (target === 'message') {
    return intl.formatMessage({ defaultMessage: 'message', id: '+Nr1ejNHzE' });
  }
  if (target === 'file') {
    return intl.formatMessage({ defaultMessage: 'file', id: 'vwxMsyPVYx' });
  }
  return intl.formatMessage({ defaultMessage: 'document', id: 'fVrLDpHixQ' });
};
